import GUIControl from "./gui_control";
import {ElementRelativePositioning} from "../dhtml_popup";
import {disableTextSelection, addClass, removeClass, getObjectWidth, getObjectHeight, whichButtonPressed} from "../atBrowserUtils";

/**
 * Dropdown button constructor.
 * @param id - button identifier, will be used as dropdown element id
 * @param caption - initial dropdown caption
 * @param dhtmlPopup - DhtmlPopup that will be shown on button press
 * @param isDropUp - if true then the dropdown will really be a drop-up :)
 * @param popupWidth - width of the popup. If null, then it will be as wide as the button (a bit narrower, really).
 * @param isOriginAtRight - if true then the popup's right side will be linked to the right side of the button.
 *                          Otherwise - popup's left side will be linked to the left side of the button.
 *                          This parameter has effect only if popupWidth is not null.
 *
 * Note: you should not set afterHide event handler for the dhtmlPopup, because it is used by the button.
 * Instead, you should use DropdownButton.setAfterPopupHideHandler().
 */
export default function DropdownButton(id, caption, dhtmlPopup, isDropUp, popupWidth, isOriginAtRight)
{
    this.base = GUIControl;
    this.base(id);
    
    this.caption = caption;
    this.dhtmlPopup = dhtmlPopup;
    this.isDropUp = isDropUp;
    this.popupWidth = popupWidth;
    this.isOriginAtRight = isOriginAtRight;

    this.pressed = false;

    if (this.dhtmlPopup)
    {
        this.dhtmlPopup.setPositioningStrategy(
                new ElementRelativePositioning(this.getId()));
        this.dhtmlPopup.setCallerElement(this);
        this.dhtmlPopup.setAfterHideHandler(function(){this.getCallerElement().unpress()});
    }

    this.normalClassName = "dropdown-button";
    this.activeClassName = "dropdown-button active";
    this.hoverClassName =  "hover";
    this.disabledClassName =  "dropdown-button disabled";
    this.savingClassName =  "dropdown-button saving";
    this.errorClassName = "error";
    this.displayErrorClass = false;

    this.beforePopupShowHandler = null;
    this.afterPopupHideHandler = null;

    this.enabled = true;
}

DropdownButton.prototype = new GUIControl;


/**
 * Sets the button caption, i.e. the text that is displayed in it.
 */
DropdownButton.prototype.setCaption = function(caption)
{
    this.caption = caption;
    this.updateContents();
}


/**
 * Returns the button caption, i.e. the text that is displayed in it.
 */
DropdownButton.prototype.getCaption = function()
{
    return this.caption;
}


/**
 * Overridden.
 */
DropdownButton.prototype.getContentsHTML = function()
{
    return this.getCaption();
}


/**
 * Overridden.
 */
DropdownButton.prototype.getHTML = function()
{
    return "<table id =  '" + this.getId() + "' " +
               "onmousedown='" + this.getId() + ".onMouseDown()' " +
               "onmouseover='" + this.getId() + ".onMouseOver()' " +
               "onmouseout='" + this.getId() + ".onMouseOut()' " +
               "class='" + this.normalClassName + "' " +
               "cellspacing='0' cellpadding='0' border='0' width='100%'>" +
               "<tr>" +
                    "<td class='top-left-border' colspan='2'></td>" +
                    "<td class='top-left-cell'></td>" +
                    "<td class='top-separator'></td>" +
                    "<td class='top-right-cell'></td>" +
                    "<td class='top-right-border' colspan='2'></td>" +
               "</tr>" +
               "<tr>" +
                    "<td class='middle-left-border'></td>" +
                    "<td class='middle-left-subborder'></td>" +
                    "<td class='middle-left-cell dropdown-button-contents text' id='" + this.getContentsContainerId() + "'>" +
                        this.getContentsHTML() + "</td>" +
                    "<td id='" + this.getId() + "_middleSeparator' class='middle-separator'></td>" +
                    "<td id='" + this.getId() + "_middleRightCell' class='middle-right-cell'><div class='arrow'></div></td>" +
                    "<td class='middle-right-subborder'></td>" +
                    "<td id='" + this.getId() + "_middleRightBorder' class='middle-right-border'></td>" +
               "</tr>" +
               "<tr>" +
                    "<td class='bottom-left-border' colspan='2'></td>" +
                    "<td class='bottom-left-cell'></td>" +
                    "<td class='bottom-separator'></td>" +
                    "<td class='bottom-right-cell'></td>" +
                    "<td class='bottom-right-border' colspan='2'></td>" +
               "</tr>" +
           "</table>";
}

DropdownButton.prototype.getButtonTable = function()
{
    return document.getElementById(this.getId());
}

/**
 * Overridden.
 * After rendering, disables text selection in the control and sets cursor to 'pointer'.
 */
DropdownButton.prototype.render = function()
{
    this.base.prototype.render.call(this);

    var element = document.getElementById(this.getId());
    disableTextSelection(element);

    // although the cursor is set in CSS, it will be reset to default in disableTextSelection(),
    // so we set it here explicitly
    element.style.cursor = "pointer";
}


/**
 * Changes the CSS class of the topmost element of the button.
 * Used by default mouse events handlers.
 */
DropdownButton.prototype.setClassName = function(className)
{
    var elem = document.getElementById(this.getId());
    if (!elem) return;
    elem.className = className;

    //a workaround for IE issue with multiple CSS classes processing
    var errorElem = elem.parentNode ? elem.parentNode : elem;
    if ( this.displayErrorClass )
        addClass( errorElem, this.errorClassName );
    else
        removeClass( errorElem, this.errorClassName );
}


/**
 * Sets handler for the event that is fired just before the dropdown popup is shown.
 * @param handler is function(), that returns 'true' if we must not show popup, 'false' or 'undefined' otherwise.
 */
DropdownButton.prototype.setBeforePopupShowHandler = function(handler)
{
    this.beforePopupShowHandler = handler;
}


/**
 * Sets handler for the event that is fired just after the dropdown popup is hidden,
 * and the button state is updated. 
 */
DropdownButton.prototype.setAfterPopupHideHandler = function(handler)
{
    this.afterPopupHideHandler = handler;
}



/**
 * Default onMouseDown handler.
 * Shows or hides the DhtmlPanel and changes button CSS class accordingly.
 */
DropdownButton.prototype.onMouseDown = function()
{
    if (!this.enabled || this.isEventProhibited()) return;

    if (whichButtonPressed(event) != 'left')
    {
        return;
    }

    if (this.pressed)
    {
        this.dhtmlPopup.setOwner(null);
        this.dhtmlPopup.hide(); // this will call unpress()
        addClass( this.getButtonTable() , this.hoverClassName);
        //this.setClassName(this.hoverClassName);
    }
    else
    {
        if (this.beforePopupShowHandler)
        {
           var handled = this.beforePopupShowHandler();

            if (handled)
                return;
        }

        this.pressed = true;
        this.setClassName(this.activeClassName);

        // here is a workaround for additional offset on the right under IE, which is caused by border
        // width of inner element
        // TODO research an opportunity for common solution, in case of need to have border width more than 1px
        var borderOffsetX = navigator.appName == 'Microsoft Internet Explorer' ? 2 : 0;
        var offsetX = (this.popupWidth && this.isOriginAtRight) ?
                        - this.popupWidth + getObjectWidth(this.getId()) - borderOffsetX:
                        2;
        var offsetY = this.isDropUp ? 1 : getObjectHeight(this.getId()) - 1;
        var width = this.popupWidth ? this.popupWidth : getObjectWidth(this.getId()) - 4;

        var target = event.target || event.srcElement;
        this.dhtmlPopup.setOwner(target);
        this.dhtmlPopup.show(offsetX, offsetY, width, null, this.isDropUp);
    }
}


/**
 * Default onMouseOver handler.
 * Changes button CSS class accordingly.
 */
DropdownButton.prototype.onMouseOver = function()
{
    if (!this.enabled || this.isEventProhibited()) return;

    if (!this.pressed)
    {
        addClass( this.getButtonTable(), this.hoverClassName );
        //this.setClassName(this.hoverClassName);
    }
}


/**
 * Default onMouseOut handler.
 * Changes button CSS class accordingly.
 */
DropdownButton.prototype.onMouseOut = function()
{
    if (!this.enabled || this.isEventProhibited()) return;

    if (!this.pressed)
    {
        removeClass( this.getButtonTable(), this.hoverClassName );
        //this.setClassName(this.normalClassName);
    }
}


/**
 * Unpresses the button, changing its state and CSS class.
 * Used as a handler for the DhtmlPanel afterHide event.
 */
DropdownButton.prototype.unpress = function()
{
    if (!this.enabled || this.isEventProhibited()) return;

    this.pressed = false;
    this.setClassName(this.normalClassName);
    if (this.afterPopupHideHandler)
        this.afterPopupHideHandler();
}

/**
 * Enables or disables listbox
 */
DropdownButton.prototype.setEnabled = function(enable)
{
    this.enabled = enable;
    
    var element = document.getElementById(this.getId());

    if (enable)
    {
        this.setClassName(this.normalClassName);
        element.style.cursor = "pointer";
    }
    else
    {
        this.setClassName(this.disabledClassName);
        element.style.cursor = "default";
    }
}

/**
 * Sets listbox in rpc saving state
 */
DropdownButton.prototype.setSaving = function(saving)
{
    var table = document.getElementById(this.getId());
    var middleSeparator = document.getElementById(this.getId() + "_middleSeparator");
    var middleRightCell = document.getElementById(this.getId() + "_middleRightCell");
    var middleRightBorder = document.getElementById(this.getId() + "_middleRightBorder");

    if (saving)
    {
        this.setClassName(this.savingClassName);
        table.style.cursor = "default";
        middleSeparator.style.display = "none";
        middleRightCell.style.display = "none";
        middleRightBorder.colSpan = 3;
    }
    else
    {
        this.setClassName(this.normalClassName);
        table.style.cursor = "pointer";
        middleSeparator.style.display = "";
        middleRightCell.style.display = "";
        middleRightBorder.colSpan = 1;
    }
}

DropdownButton.prototype.setDisplayError = function( display )
{
    this.displayErrorClass = display;
}


DropdownButton.prototype.isEventProhibited = function()
{
    return false;
}



// WEBPACK FOOTER //
// ./web/js/controls/dropdown_button.js